import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const TabContext = createContext(null);

export const TabProvider = ({ children }) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [routeInfoByPath, setRouteInfoByPath] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const getTabIdentityPath = useCallback((path) => {
    return path;
  }, []);

  const registerMenuRoutes = useCallback((menuItems = []) => {
    const nextRouteInfo = {};

    const addRouteInfo = (item) => {
      if (!item?.route) return;
      nextRouteInfo[item.route] = {
        title: item.translationKey || item.name || item.route,
        icon: item.icon || 'fa-file',
      };
    };

    menuItems.forEach((menu) => {
      addRouteInfo(menu);
      (menu.submenus || []).forEach(addRouteInfo);
    });

    const findRouteInfo = (path) => {
      if (nextRouteInfo[path]) return nextRouteInfo[path];
      const matchingRoute = Object.keys(nextRouteInfo)
        .filter((route) => path.startsWith(`${route}/`) || path.startsWith(`${route}-`))
        .sort((a, b) => b.length - a.length)[0];
      return matchingRoute ? nextRouteInfo[matchingRoute] : null;
    };

    setRouteInfoByPath(nextRouteInfo);
    setTabs((currentTabs) => currentTabs.map((tab) => {
      const routeInfo = findRouteInfo(tab.path);
      return routeInfo ? { ...tab, ...routeInfo } : tab;
    }));
  }, []);

  const getRouteInfo = useCallback((path) => {
    if (routeInfoByPath[path]) return routeInfoByPath[path];

    const matchingRoute = Object.keys(routeInfoByPath)
      .filter((route) => path.startsWith(`${route}/`) || path.startsWith(`${route}-`))
      .sort((a, b) => b.length - a.length)[0];

    return matchingRoute ? routeInfoByPath[matchingRoute] : { title: path, icon: 'fa-file' };
  }, [routeInfoByPath]);

  // Abrir una nueva pestaña
  const openTab = useCallback((path, title = null, icon = null, options = {}) => {
    const { navigateTo = true } = options;
    const tabPath = getTabIdentityPath(path);
    const routeInfo = getRouteInfo(tabPath);
    const tabTitle = title || routeInfo.title;
    const tabIcon = icon || routeInfo.icon;

    const existingTab = tabs.find(tab => tab.path === tabPath);
    if (existingTab) {
      if (existingTab.id !== activeTabId) {
        setActiveTabId(existingTab.id);
      }
      if (navigateTo) {
        navigate(path);
      }
      return;
    }

    const newTab = {
      id: `tab-${Date.now()}-${Math.random()}`,
      path: tabPath,
      title: tabTitle,
      icon: tabIcon,
    };

    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabId(newTab.id);
    if (navigateTo) {
      navigate(path);
    }
  }, [tabs, activeTabId, navigate, getRouteInfo, getTabIdentityPath]);

  // Cerrar una pestaña
  const closeTab = useCallback((tabId) => {
    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    if (tabIndex === -1) return;

    const newTabs = tabs.filter(tab => tab.id !== tabId);
    setTabs(newTabs);

    // Si se cierra la pestaña activa, activar otra
    if (activeTabId === tabId) {
      if (newTabs.length > 0) {
        // Activar la pestaña anterior o la siguiente
        const newActiveIndex = tabIndex > 0 ? tabIndex - 1 : 0;
        const newActiveTab = newTabs[newActiveIndex];
        setActiveTabId(newActiveTab.id);
        navigate(newActiveTab.path);
      } else {
        // Si no hay más pestañas, ir al dashboard
        setActiveTabId(null);
        navigate('/dashboard');
      }
    }
  }, [tabs, activeTabId, navigate]);

  // Activar una pestaña
  const activateTab = useCallback((tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      setActiveTabId(tabId);
      navigate(tab.path);
    }
  }, [tabs, navigate]);

  // Cerrar todas las pestañas excepto la activa
  const closeOtherTabs = useCallback(() => {
    if (activeTabId) {
      setTabs(prevTabs => prevTabs.filter(tab => tab.id === activeTabId));
    }
  }, [activeTabId]);

  // Cerrar todas las pestañas
  const closeAllTabs = useCallback(() => {
    setTabs([]);
    setActiveTabId(null);
    navigate('/dashboard');
  }, [navigate]);

  // Sincronizar pestaña activa con la ruta actual
  React.useEffect(() => {
    const currentPath = getTabIdentityPath(location.pathname);
    const currentTab = tabs.find(tab => tab.path === currentPath);
    if (currentTab && currentTab.id !== activeTabId) {
      setActiveTabId(currentTab.id);
    } else if (!currentTab && location.pathname !== '/') {
      // Si la ruta cambia pero no hay pestaña, crear una
      openTab(currentPath, null, null, { navigateTo: false });
    }
  }, [location.pathname, tabs, activeTabId, openTab, getTabIdentityPath]);

  const value = {
    tabs,
    activeTabId,
    openTab,
    registerMenuRoutes,
    closeTab,
    activateTab,
    closeOtherTabs,
    closeAllTabs,
  };

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

export const useTabs = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabs must be used within a TabProvider');
  }
  return context;
};

